import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';

import { Loggable } from 'app/utils/logging/loggable';
import Social from 'entity/Social';
import SocialRoomSet from 'entity/SocialRoomSet';

import { actions } from '../state/app';

import { VideoChatService } from './videochat.service';

/**
 * Checks if a room is in a set of rooms
 * @param roomSet The set of rooms to check
 * @param roomId The roomId to check for
 * @returns True if the room is in the set, false otherwise
 */
const isRoomInSet = (roomSet: SocialRoomSet, roomId: string): boolean => {
  const { socials = [] } = roomSet ?? {};
  const roomIds = socials?.map((s) => s.socialId) ?? [];
  return roomIds.includes(roomId);
};

@Injectable()
export class SocialResolverService extends Loggable {
  private roomSet?: SocialRoomSet;
  private isEditing? = false;

  public constructor(
    public videoChatService: VideoChatService,
    public router: Router,
    private store: Store,
  ) {
    super();
    this.setDisplayName('SocialResolverService');
  }

  public async resolve(route: ActivatedRouteSnapshot): Promise<Social | null> {
    this.resetState();
    this.isEditing = route.url.some((segment) => segment.path === 'settings' || segment.path === 'edit');

    const roomId = route.paramMap.get('id');
    const roomSetId = route.paramMap.get('sid');

    if (roomSetId) {
      await this.loadSocialRoomSet(roomSetId);
      if (!this.roomSet) {
        await this.router.navigate(['404']);
        return null;
      }
      if (roomId) {
        const isRoomIdInSet = isRoomInSet(this.roomSet, roomId);
        if (!isRoomIdInSet) {
          // If we have an invalid room but a valid room set, redirect to the set.
          await this.router.navigate(['connected-room', this.roomSet?.socialRoomSetId]);
          return null;
        }
      }
    }

    // deprecated, legacy route support
    if (roomId) {
      return await this.routeByRoomId(roomId);
    }
    if (roomSetId) {
      return await this.routeBySet();
    }

    return null;
  }

  /**
   * Reset local state from previous resolutions
   */
  private resetState(): void {
    this.isEditing = false;
    this.roomSet = undefined;
  }

  /**
   * When room ID is present in URL, retrieves and stores room information and routes by the room Id.
   * If set is present, also retrieves and stores set information.
   * @param id - the social/room id to store and route to.
   * @param sid - optionally, provide when we are routing by a provided room id as part of a set, but set has not been retrieved yet.
   * @returns the active Social/Room
   */
  // @deprecated - legacy route support
  private async routeByRoomId(id: string): Promise<Social | null> {
    try {
      const socialMatch = await firstValueFrom(this.videoChatService.matchSocial(id));
      const social = socialMatch && socialMatch.social;

      const socialId = social && social.socialId;

      if (social && !social.isValid) {
        this.store.dispatch(actions.LOAD_SOCIAL({ social }));
        return social;
      }

      if (socialId) {
        const region = await this.videoChatService.getRegion();
        const socialFromId = await firstValueFrom(this.videoChatService.getSocial(socialId, true, region));
        const { socialRoomSetId } = socialFromId;

        if (!this.roomSet && socialRoomSetId && !this.isEditing) {
          const roomSet = this.roomSet ?? (await this.loadSocialRoomSet(socialRoomSetId));
          if (!roomSet) {
            throw new Error('Failed to load room set');
          }

          const { socials = [] } = roomSet;
          if (socials.length > 1) {
            await this.router.navigate(['connected-room', socialFromId.socialRoomSetId, socialFromId.socialId]);
            return null;
          }
        }

        this.store.dispatch(actions.LOAD_SOCIAL({ social: socialFromId }));
        return socialFromId;
      }
    } catch (e) {
      this.warn('404: Failed to load room', { id, set: this.roomSet, e });
      await this.router.navigate(['404']);
    }
    return null;
  }

  /**
   * When room ID is not provided, use the set information to get the first room associated with a connected room set,
   * store it and route accordingly.
   * @returns the active Social/Room
   */
  private async routeBySet(): Promise<Social | null> {
    if (!this.roomSet) {
      this.error('Attempted to route by connected room set, but no set found');
      await this.router.navigate(['404']);
      return null;
    }

    const socialsFromSet = this.roomSet?.socials;

    if (socialsFromSet && socialsFromSet[0].socialId) {
      return await this.routeByRoomId(socialsFromSet[0].socialId);
    } else {
      this.error('404: Social Room Set has no rooms', { sid: this.roomSet.socialRoomSetId });
      await this.router.navigate(['404']);
      return null;
    }
  }

  /**
   * Retrieves & stores the connected room set.
   * @param roomSetId: ID of the room set to retrieve
   */
  private async loadSocialRoomSet(roomSetId: string): Promise<SocialRoomSet | undefined> {
    try {
      const socialRoomSet = await firstValueFrom(this.videoChatService.getSocialRoomSet(roomSetId));
      this.roomSet = socialRoomSet;
      this.store.dispatch(actions.LOAD_CONNECTED_ROOM_SET({ set: socialRoomSet }));
      return socialRoomSet;
    } catch (e) {
      this.warn('404: Failed to load room set', { sid: roomSetId, e });
    }
  }

  /**
   * Only retrieves the connected room set.
   * @param roomSetId: ID of the room set to retrieve
   */
  public async retrievesSocialRoomSet(roomSetId: string): Promise<SocialRoomSet | undefined> {
    try {
      const socialRoomSet = await firstValueFrom(this.videoChatService.getSocialRoomSet(roomSetId));
      return socialRoomSet;
    } catch (e) {
      this.warn('404: Failed to retrieve room set', { sid: roomSetId, e });
    }
  }
}
